/**
 * =============================================================================
 * ROASTBREW - PLAYLISTS HOME  ·  /playlist
 * =============================================================================
 *
 * The student's playlist suggestions page:
 *   - Hero: "Continue where you left off" — the most recently watched playlist
 *   - Rows: Recent playlists · Saved playlists · Suggested playlists
 *
 * NOT WIRED YET. Content comes from data/playlistSample.js (the one place to swap
 * for real data). Thumbnails are REAL catalog images when the catalog has loaded;
 * otherwise each card shows a plain title card. Search filters the sample rows on
 * this page only.
 *
 * Access: gated in App.jsx (RequireFeature 'playlists'). No tier logic here.
 * Renders within AppLayout (which passes isMobile / isTablet).
 * =============================================================================
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import {
  SAMPLE_HOME, getSamplePlaylist, numberItems, catalogThumbnails, newPlaylistCode,
} from './data/playlistSample';

const SANS = '"DM Sans", sans-serif';
const SERIF = '"Cormorant Garamond", serif';

// Deep colour fields for the fallback title cards (stand-ins for thumbnails).
const FIELDS = [['#24354F', '#1A2638'], ['#5A2A2B', '#3B1C1D'], ['#2C4636', '#1C2E23'], ['#6E5222', '#4A3716'], ['#45304A', '#2D1F31'], ['#1F4447', '#142D2F']];

// A 16:9 thumbnail: a real catalog image when we have one, else a title card.
function Thumb({ src, course, title, index, big = false, children }) {
  const [a, b] = FIELDS[index % FIELDS.length];
  return (
    <span style={{
      position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box',
      aspectRatio: '16 / 9', padding: big ? '20px 22px 24px' : '12px 14px 14px', borderRadius: big ? '16px' : '10px', overflow: 'hidden',
      background: `radial-gradient(ellipse at 85% 15%, rgba(243,239,230,0.10) 0%, transparent 55%), linear-gradient(140deg, ${a} 0%, ${b} 100%)`,
    }}>
      {src ? (
        <img src={src} alt="" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <>
          <span style={{ position: 'relative', fontFamily: SANS, fontSize: big ? '11px' : '9.5px', fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(243,239,230,0.72)' }}>{course}</span>
          <span style={{ position: 'relative', fontFamily: SERIF, fontSize: big ? '32px' : '19px', lineHeight: 1.08, color: '#F3EFE6' }}>{title}</span>
        </>
      )}
      {children}
    </span>
  );
}

// A playlist card: a thumbnail with two thin layers above it (a stack of videos),
// a lesson-count badge, and the card's own detail line(s).
function PlaylistCard({ card, index, thumb, colors, onOpen, extra }) {
  return (
    <button type="button" className="pl-card" onClick={onOpen} style={{
      display: 'flex', flexDirection: 'column', gap: '12px', padding: 0, border: 0, background: 'transparent',
      textAlign: 'left', cursor: 'pointer', color: colors.text, minWidth: 0,
    }}>
      <span style={{ position: 'relative', display: 'block', paddingTop: '10px' }}>
        <span aria-hidden="true" style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: '6px', borderRadius: '6px 6px 0 0', background: FIELDS[index % FIELDS.length][0], opacity: 0.4 }} />
        <span aria-hidden="true" style={{ position: 'absolute', top: '4px', left: '6%', right: '6%', height: '6px', borderRadius: '6px 6px 0 0', background: FIELDS[index % FIELDS.length][0], opacity: 0.7 }} />
        <Thumb src={thumb} course={card.course} title={card.firstLesson} index={index}>
          <span style={{
            position: 'absolute', right: '8px', bottom: '8px', display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '3px 8px', borderRadius: '6px', background: 'rgba(10,10,10,0.72)', fontFamily: SANS, fontSize: '11.5px', fontWeight: 600, color: '#F3EFE6',
          }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
            {card.count}<span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}> lessons</span>
          </span>
        </Thumb>
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontFamily: SANS }}>
        <span style={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.3 }}>{card.title}</span>
        <span style={{ fontSize: '12.5px', color: colors.muted }}>{card.author} · {card.count} lessons</span>
        {extra}
      </span>
    </button>
  );
}

export default function PlaylistsHomePage({ isMobile = false, isTablet = false }) {
  const { colors, isLight } = useTheme();
  const { catalog } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const edge = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const surface = colors.cardBg || colors.bgAlt;
  const track = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)';

  // Real catalog thumbnails, handed out in turn so each card shows a different one.
  const thumbs = useMemo(() => catalogThumbnails(catalog), [catalog]);
  const thumbAt = (i) => (thumbs.length ? thumbs[i % thumbs.length] : null);

  // Hero: the playlist (and the lesson in it) to continue with.
  const heroPlaylist = getSamplePlaylist(SAMPLE_HOME.continue.code);
  const heroItems = numberItems(heroPlaylist ? heroPlaylist.items : []);
  const heroItem = heroItems[SAMPLE_HOME.continue.item - 1] || null;
  const heroLessons = heroItems.filter((i) => i.type === 'lesson');
  const heroDone = heroItem && heroItem.lessonNo ? heroItem.lessonNo - 1 : 0;

  // Search filters the sample rows by title or author (this page only, for now).
  const q = query.trim().toLowerCase();
  const match = (c) => !q || `${c.title} ${c.author}`.toLowerCase().includes(q);
  const rows = [
    { key: 'recent', title: 'Recent playlists', cards: SAMPLE_HOME.recent.filter(match) },
    { key: 'saved', title: 'Saved playlists', cards: SAMPLE_HOME.saved.filter(match) },
    { key: 'suggested', title: 'Suggested playlists', cards: SAMPLE_HOME.suggested.filter(match) },
  ];
  const nothingFound = q && rows.every((r) => r.cards.length === 0);

  const cols = isMobile ? 'repeat(2, minmax(0, 1fr))' : isTablet ? 'repeat(3, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))';
  const open = (code) => navigate(`/playlist/${code}`);
  const create = () => navigate(`/playlist/${newPlaylistCode()}/edit`);

  let cardIndex = 0;
  return (
    <div style={{
      padding: isMobile ? '80px 16px 40px' : isTablet ? '80px 32px 48px' : '40px 48px 72px',
      minHeight: '100vh', width: '100%', maxWidth: '100%', boxSizing: 'border-box',
      background: colors.bg, color: colors.text, animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      <div style={{ maxWidth: '1048px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: isMobile ? '36px' : '52px' }}>

        {/* Top line: heading, search, create */}
        <header style={{ display: 'flex', flexWrap: isMobile ? 'wrap' : 'nowrap', alignItems: 'center', gap: isMobile ? '14px' : '20px' }}>
          <h1 style={{ margin: 0, flexShrink: 0, fontFamily: SERIF, fontWeight: 300, fontSize: isMobile ? '36px' : '46px', lineHeight: 1 }}>Playlists</h1>
          <label htmlFor="pl-home-search" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Search playlists</label>
          <div style={{
            order: isMobile ? 3 : 0, flex: isMobile ? '1 1 100%' : '1 1 auto', display: 'flex', alignItems: 'center', gap: '12px',
            height: '48px', padding: '0 20px', borderRadius: '999px', background: surface, border: `1px solid ${edge}`,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={colors.muted} strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input id="pl-home-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search playlists"
              style={{ flex: 1, minWidth: 0, height: '100%', border: 0, outline: 'none', background: 'transparent', color: colors.text, fontFamily: SANS, fontSize: '15px' }} />
          </div>
          <button type="button" onClick={create} style={{
            marginLeft: isMobile ? 'auto' : 0, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '10px', height: '48px', padding: '0 20px',
            borderRadius: '999px', border: `1px solid ${colors.accent}`, background: 'transparent', color: colors.accent,
            fontFamily: SANS, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
            Create playlist
          </button>
        </header>

        {/* Hero: continue where you left off */}
        {heroPlaylist && heroItem && !q && (
          <section aria-label="Continue where you left off" style={{
            display: 'grid', gridTemplateColumns: isMobile || isTablet ? '1fr' : '460px minmax(0, 1fr)', gap: isMobile ? '22px' : '40px',
            alignItems: 'center', padding: isMobile ? '16px' : '28px', borderRadius: '24px', background: surface, border: `1px solid ${edge}`,
          }}>
            <button type="button" className="pl-card" aria-label={`Continue: ${heroItem.title}`}
              onClick={() => navigate(`/playlist/${heroPlaylist.code}/${SAMPLE_HOME.continue.item}`)}
              style={{ padding: 0, border: 0, background: 'transparent', cursor: 'pointer', display: 'block', textAlign: 'left' }}>
              <Thumb src={thumbAt(99)} course={`${heroItem.course} · Lesson ${heroItem.lessonNo}`} title={heroItem.title} index={0} big>
                <span aria-hidden="true" style={{
                  position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(10,10,10,0.55)', border: '1px solid rgba(243,239,230,0.35)',
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="#F3EFE6" style={{ marginLeft: '3px' }}><path d="M7 4l13 8-13 8z" /></svg>
                </span>
                <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '4px', background: 'rgba(243,239,230,0.18)' }}>
                  <span style={{ display: 'block', height: '100%', width: '38%', background: colors.accent }} />
                </span>
              </Thumb>
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
              <span style={{ fontFamily: SANS, fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.accent }}>Continue where you left off</span>
              <h2 style={{ margin: 0, fontFamily: SERIF, fontWeight: 300, fontSize: isMobile ? '36px' : '48px', lineHeight: 1 }}>{heroPlaylist.title}</h2>
              <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: '19px', color: colors.textSecondary }}>by {heroPlaylist.author}</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px', fontFamily: SANS }}>
                <span style={{ fontSize: '14px', color: colors.textSecondary }}>
                  Up next: <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: '17px', color: colors.accent }}>§ {heroItem.lessonNo}</span> {heroItem.title} <span style={{ color: colors.muted }}>· {heroItem.length}</span>
                </span>
                <span style={{ display: 'block', height: '4px', borderRadius: '4px', background: track, overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${Math.round((heroDone / Math.max(heroLessons.length, 1)) * 100)}%`, background: colors.accent }} />
                </span>
                <span style={{ fontSize: '12.5px', color: colors.muted }}>{heroDone} of {heroLessons.length} lessons watched</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '8px' }}>
                <button type="button" onClick={() => navigate(`/playlist/${heroPlaylist.code}/${SAMPLE_HOME.continue.item}`)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px', height: '50px', padding: '0 28px', border: 0, borderRadius: '999px',
                  background: colors.accent, color: isLight ? '#FFFFFF' : '#0D0D0D', fontFamily: SANS, fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
                  Continue
                </button>
                <button type="button" onClick={() => open(heroPlaylist.code)} style={{
                  height: '50px', padding: '0 22px', borderRadius: '999px', border: `1px solid ${edge}`, background: 'transparent',
                  color: colors.textSecondary, fontFamily: SANS, fontSize: '14.5px', fontWeight: 500, cursor: 'pointer',
                }}>View playlist</button>
              </div>
            </div>
          </section>
        )}

        {/* Rows */}
        {rows.map((row) => row.cards.length > 0 && (
          <section key={row.key} aria-label={row.title} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontFamily: SERIF, fontWeight: 400, fontSize: isMobile ? '26px' : '30px' }}>{row.title}</h2>
              {/* "See all" pages don't exist yet; kept so the layout reads true. */}
              <span style={{ fontFamily: SANS, fontSize: '14px', fontWeight: 500, color: colors.muted }}>See all</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: cols, gap: isMobile ? '18px 14px' : '24px' }}>
              {row.cards.map((card) => {
                const i = cardIndex++;
                const extra = row.key === 'recent' ? (
                  <>
                    <span style={{ display: 'block', height: '3px', marginTop: '6px', borderRadius: '3px', background: track, overflow: 'hidden' }}>
                      <span style={{ display: 'block', height: '100%', width: `${Math.round((card.done / card.count) * 100)}%`, background: colors.accent }} />
                    </span>
                    <span style={{ fontSize: '12px', color: colors.muted }}>{card.done} of {card.count} watched</span>
                  </>
                ) : row.key === 'saved' ? (
                  <span style={{ fontSize: '12px', color: colors.muted }}>{card.when}</span>
                ) : (
                  <span style={{ fontSize: '12px', fontStyle: 'italic', color: colors.textSecondary }}>{card.why}</span>
                );
                return <PlaylistCard key={card.code} card={card} index={i + 1} thumb={thumbAt(i)} colors={colors} onOpen={() => open(card.code)} extra={extra} />;
              })}
            </div>
          </section>
        ))}

        {nothingFound && (
          <p style={{ margin: 0, fontFamily: SANS, fontSize: '15px', color: colors.muted }}>
            No playlists match “{query}”. Try a course name, a topic, or the name of the person who made it.
          </p>
        )}
      </div>

      <style>{`
        .pl-card:focus-visible, .pl-page button:focus-visible { outline: 2px solid ${colors.accent}; outline-offset: 4px; border-radius: 12px; }
        #pl-home-search::placeholder { color: ${colors.muted}; }
        @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important; } }
      `}</style>
    </div>
  );
}
